//#region [IMPORT]

// === THIRD-PARTY ===
import React, { createContext, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actions as adminActions } from 'store/modules/admin';

// === API ===
import { prokeyApi } from 'api/prokey';

// === COMPONENTS ===
import { useTheme } from '@mui/material/styles';
import { AddUserDialog } from './AddUserDialog';
import { BulkAddUserDialog } from './BulkAddUserDialog';
import { UserRow } from './UserRow';
import { BulkEditUsersDialog } from './BulkEditUsersDialog';

import {
  Box,
  Button,
  Checkbox,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  InputAdornment,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SearchIcon from '@mui/icons-material/Search';
import GroupsIcon from '@mui/icons-material/Groups';
import DownloadIcon from '@mui/icons-material/Download';
import {onRoles, onUsers, onDisabledUsers, removeListener, requestRoles, requestUsers, requestDisabledUsers, userDisable, userEnable, requestUserListDownload, onUserListDownload} from 'api/prokey/prokey.websocket';
import dayjs from 'dayjs';

//#endregion

//-----------------------------------------------------------------
export function UsersPanel() {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const theme = useTheme();

  const [ users , setUsers ] = useState( [] );
  const [ roles , setRoles ] = useState( [] );
  const [ disabledUsers , setDisabledUsers ] = useState( [] );

  const [ is_open_add_users_bulk , setShowBulkAddUsersModal ] = useState( false );
  const [ is_open_add_user       , setShowAddUsersModal     ] = useState( false );
  const [ is_open_edit_users_bulk, setShowBulkEditUsersModal] = useState( false );
  const [ is_disabled_users      , setShowDisabledUsers     ] = useState( false );
  const [ filter_text            , setFilterText            ] = useState( '' );

  const [ select_all , setSelectAll ] = useState( false );
  const [ selected_users , setSelectedUsers ] = useState( {} );
  
  const toggle_users_text = is_disabled_users ? 'Show Enabled Users' : 'Show Disabled Users';
  const toggle_users_icon = is_disabled_users ? <VisibilityIcon />   : <VisibilityOffIcon />;
    
  //#endregion

  //#region [STYLES]

  const _styles = {
    container: {
      width  : '100%',
      height: '100%',
      margin : theme.spacing(0),
    },
    control_container: {
      borderRadius: '1rem',
      display: 'flex',
      width: '100%',
      padding: '0.5rem',
      marginBottom: '1rem',
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    },
    show_user_button: {
      margin: '1rem',
    },
    search_user: {
      margin: '1rem',
      marginLeft: 'auto',
      width: '30rem',
    },
    button_add_users_bulk: {
      margin: '1rem',
      backgroundColor: theme.palette.success.main,
      '&:hover': {
        backgroundColor: theme.palette.success.dark,
      },
    },
    button_edit_users_bulk: {
      margin: '1rem',
      backgroundColor: theme.palette.success.main,
      '&:hover': {
        backgroundColor: theme.palette.success.dark,
      },
    },
    button_add_users: {
      margin: '1rem',
      backgroundColor: theme.palette.success.main,
      '&:hover': {
        backgroundColor: theme.palette.success.dark,
      },
    },
    button_download_users: {
      margin: '1rem',
    },
    table_users: {
      width: '100%',
      height: '100%',
      borderRadius: '1rem',
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    },
    table_header_cell: {
      borderBottom: '2px solid ' + theme.palette.primary.main,
      color: theme.palette.primary.main,
    }
  }

  const CONTAINER = {
    sx : _styles.container,
  }
  const DIALOG_ADD_USER = {
    showDialog    : is_open_add_user    ,
    setShowDialog : setShowAddUsersModal ,
    // refreshUsers  : refreshUsers         ,
  }
  const DIALOG_ADD_USER_BULK = {
    showDialog    : is_open_add_users_bulk    ,
    setShowDialog : setShowBulkAddUsersModal ,
    // refreshUsers  : refreshUsers             ,
  }
  const DIALOG_EDIT_USERS_BULK = {
    roles         : roles,
    users         : Object.keys( selected_users ),
    showDialog    : is_open_edit_users_bulk   ,
    setShowDialog : setShowBulkEditUsersModal ,
  }

  const CONTAINER_CONTROLS = {
    sx : _styles.control_container,
  }
  const BUTTON_SHOW_USER  = {
    sx        : _styles.show_user_button,
    variant   :'contained'  ,
    startIcon : toggle_users_icon,
    onClick   : toggleShowDisabledUsers,
    children  : [ toggle_users_text ],
    name      : 'toggle_show_users_btn',
  }
  const BUTTON_DOWNLOAD_USERS  = {
    sx        : _styles.button_download_users,
    variant   :'contained'  ,
    startIcon : <DownloadIcon />,
    onClick  : () => requestUserListDownload(),
    children  : [ 'Download user list (.CSV)' ],
    name      : 'download_user_list_btn',
  }

  const search_input_props = {
    startAdornment: (
      <InputAdornment position='start'>
        <SearchIcon />
      </InputAdornment>
    ),
  }
  const SEARCH_USER = {
    sx           : _styles.search_user,
    id           : 'input-with-icon-textfield',
    label        : 'Search Users',
    autoComplete : 'off',
    InputProps   : search_input_props,
    onChange     : (e) => {setFilterText(e.target.value); setSelectedUsers( {} );},
    name         : 'search_users_input',
  }
  const BUTTON_ADD_USERS_BULK = {
    sx        : _styles.button_add_users_bulk,
    variant   : 'contained',
    startIcon : <GroupAddIcon />,
    onClick   : () => setShowBulkAddUsersModal(true),
    children  : [ 'Bulk Add Users' ],
    name      : 'add_bulk_users_btn',
  }
  const BUTTON_ADD_USERS = {
    sx        : _styles.button_add_users,
    variant   : 'contained',
    startIcon : <PersonAddIcon />,
    onClick   : () => setShowAddUsersModal(true),
    children  : [ 'Add User' ],
    name      : 'add_user_btn',
  }
  const BUTTON_EDIT_USERS_BULK = {
    sx        : _styles.button_edit_users_bulk,
    variant   : 'contained',
    startIcon : <GroupsIcon />,
    disabled  : Object.keys( selected_users ).length <= 0,
    onClick   : () => setShowBulkEditUsersModal(true),
    children  : [ 'Bulk Edit Users' ],
  }
  const TABLE_USERS = {
    sx : _styles.table_users,
    size : 'small',
  }

  //#endregion

  //#region [EFFECT] 

  useEffect(() => {
    //-----------------------------------------------------------------
    function handleUsers( data ) {
      //-----------------------------------------------------------------
      setUsers( data);
    };

    //-----------------------------------------------------------------
    function handleRoles( data ) {
      //-----------------------------------------------------------------
      setRoles( data );
    }

    //-----------------------------------------------------------------
    function handleDisabledUsers( data ) {
      //-----------------------------------------------------------------
      setDisabledUsers(data);
    }

    //-----------------------------------------------------------------
    function handleUserListDownload( data ){
      //-----------------------------------------------------------------
      downloadCsv(data);
    }
  
    onUsers( handleUsers );
    onRoles( handleRoles );   
    onDisabledUsers(handleDisabledUsers); 
    onUserListDownload( handleUserListDownload );

    requestUsers();
    requestRoles();   
    
     return () => {
      removeListener( 'USERS-REQUEST', handleUsers );
      removeListener( 'ROLES-REQUEST', handleRoles );
      removeListener( 'DISABLED-USERS-REQUEST', handleDisabledUsers );
      removeListener( 'USER-LIST-DOWNLOAD-REQUEST', handleUserListDownload );
    }
  }, []);


  //#endregion

  //#region [MODIFY]

  //-----------------------------------------------------------------
  function toggleShowDisabledUsers() {
    //-----------------------------------------------------------------   
    requestUsers( !is_disabled_users );

    setShowDisabledUsers(!is_disabled_users);  
  };

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function handleUserSelect(id, is_selected) {
    //-----------------------------------------------------------------
    const temp = {...selected_users}
    if( is_selected ) {
      temp[id] = true;
    } else {
      delete temp[id];
    }

    setSelectedUsers( temp );
  }

  //#endregion

  //-----------------------------------------------------------------
  async function resetUserPassword(id) {
    //-----------------------------------------------------------------
    if ( !id ) return;

    try {
      const response = await prokeyApi.updateUsers( 'reset', [id] );
    } catch( error ) {
      console.warn( error );
    }
  };

  //-----------------------------------------------------------------
  // function resetUserPassword(id) {
  //   //----------------------------------------------------------------- 
  //   passwordReset(id);
  // };
  
  // -----------------------------------------------------------------
  function disableUser(id) {
    //-----------------------------------------------------------------  
    userDisable(id);
  };

  //-----------------------------------------------------------------
  function enableUser(id) {
    //----------------------------------------------------------------- 
    userEnable(id);
  };

  //-----------------------------------------------------------------
  function modifyUser(action, id) {
    //-----------------------------------------------------------------
         if ( action === 'reset'   ) resetUserPassword(id);
    else if ( action === 'disable' ) disableUser(id);
    else if ( action === 'enable'  ) enableUser(id);
  };

  // Properties of User that should be checked for filter text
  const filter_properties = ['Username', 'FirstName', 'LastName', 'Email', 'Company', 'Roles'];
  //-----------------------------------------------------------------
  function filterLogic( user ) {
    //-----------------------------------------------------------------
    const filter_text_lower = filter_text.toLowerCase();
    const has_filter_text = filter_properties.some((property) => {
      if ( property === 'Roles' ) {
        return user.roles.some((role) =>
          role.toLowerCase().includes(filter_text_lower)
        );
      }
      return user[property]?.toLowerCase().includes(filter_text_lower);
    });

    return has_filter_text;
  };

  //#endregion

  //#region [RENDER]

  // ------------------------------------------------------
  // HEADERS

  //-----------------------------------------------------------------
  function displayHeaders() {
    //-----------------------------------------------------------------
    const SelectAll = displaySelectionBox;

    return (
      <TableHead>
        <TableRow>
          <TableCell sx={_styles.table_header_cell} ><SelectAll /></TableCell>
          <TableCell sx={_styles.table_header_cell} >{ ''                }</TableCell>
          <TableCell sx={_styles.table_header_cell} >{ 'Username'        }</TableCell>
          <TableCell sx={_styles.table_header_cell} >{ 'First Name'      }</TableCell>
          <TableCell sx={_styles.table_header_cell} >{ 'Last Name'       }</TableCell>
          <TableCell sx={_styles.table_header_cell} >{ 'Email'           }</TableCell>
          <TableCell sx={_styles.table_header_cell} >{ 'Company'         }</TableCell>
          <TableCell sx={_styles.table_header_cell} >{ 'Password Expire' }</TableCell>
          <TableCell sx={_styles.table_header_cell} >{ 'Roles'           }</TableCell>
        </TableRow>
      </TableHead>
    );
  }
  
  //-----------------------------------------------------------------
  function displaySelectionBox() {
    //-----------------------------------------------------------------
    return (
      <Checkbox checked={select_all} onChange={
        (event)=>{
          setSelectAll(event.target.checked);
          const temp = {...selected_users}

          let disp_users = users;
          if (filter_text) disp_users = disp_users.filter(filterLogic);
          disp_users = disp_users.sort((a, b) => (a.Username > b.Username ? 1 : -1));
          for( let user of disp_users ) {
            if( event.target.checked ) {
              temp[user.Id] = true;
            } else {
              delete temp[user.Id];
            }
          }

          setSelectedUsers( temp );
        }
      }/>
    )
  }

  // ------------------------------------------------------
  // USER ROWS

  //-----------------------------------------------------------------
  function displayUserRows() {
    //-----------------------------------------------------------------
    let user_rows = [];

    if (!users) return null;

    let disp_users = users;
    if (filter_text) disp_users = disp_users.filter(filterLogic);
    disp_users = disp_users.sort((a, b) => (a.Username > b.Username ? 1 : -1));

    for (const user of disp_users) {
      user_rows.push(
        <UserRow
          key={user.Id}
          user={user}
          roles={roles}
          // refreshUsers={refreshUsers}
          modifyUser={modifyUser}
          is_selected={selected_users[user.Id]=== true}
          onSelected={handleUserSelect}
        />
      );
    }

    return <TableBody sx={{ width: '100%' }}>{user_rows}</TableBody>;
  }

  //-----------------------------------------------------------------
  function downloadCsv(csv_string) {
    //-----------------------------------------------------------------
    const file_name     = `users-${ dayjs().format('YYYYMMDD') }.csv`;
    const csv_blob      = new Blob( [csv_string], { type: 'text/csv' } );
    const csv_url       = window.URL.createObjectURL( csv_blob );
    const csv_hyperlink = document.createElement('a');

    csv_hyperlink.setAttribute('href'    , csv_url  );
    csv_hyperlink.setAttribute('download', file_name);
    csv_hyperlink.click();
  };

  // ------------------------------------------------------
  // DISPLAY

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------

    return (
      <Box { ...CONTAINER } >
        <AddUserDialog       { ...DIALOG_ADD_USER        } />
        <BulkAddUserDialog   { ...DIALOG_ADD_USER_BULK   } />
        <BulkEditUsersDialog { ...DIALOG_EDIT_USERS_BULK } />

        <Box { ...CONTAINER_CONTROLS } >
          <Button    { ...BUTTON_SHOW_USER       } />
          <Button    { ...BUTTON_DOWNLOAD_USERS  } />
          <TextField { ...SEARCH_USER            } />
          <Button    { ...BUTTON_EDIT_USERS_BULK } />
          <Button    { ...BUTTON_ADD_USERS_BULK  } />
          <Button    { ...BUTTON_ADD_USERS       } />
        </Box>

        <Box sx={{ height: '100%' }}>
          <Table { ...TABLE_USERS } >
            {displayHeaders()}
            {displayUserRows()}
          </Table>
        </Box>
      </Box>
    );
  }

  //#endregion

  return display();
}